import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader, Database, Shield, Clock, Terminal, CheckCircle, History, Eye, Zap, Search } from 'lucide-react';
import api from '../services/api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';

/**
 * Z-Index Layer System (keep in sync with FeedbackWidget)
 * --------------------------------------------------------
 * dropdown = 100, sidebar = 200, console = 500, 
 * feedbackButton = 800, nps = 900, modal = 1000, toast = 1100
 * 
 * Gabhan Console uses z-[500] to stay below modals but above content
 * 
 * Theme Support: Uses CSS variables from index.css (--console-*)
 * Events: Dispatches 'gabhan-console-toggle' when opened/closed
 */

const ChatAssistant = () => {
  const isDevelopment = import.meta.env.DEV;
  const { user } = useAuth();
  const roleName = typeof user?.role === 'string' ? user.role : user?.role?.name;
  const roleLabel = typeof roleName === 'string' && roleName.trim() ? roleName : 'User';
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionButtons, setActionButtons] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [connectionLatency, setConnectionLatency] = useState(null);
  const [commandHistory, setCommandHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentMode, setCurrentMode] = useState('query'); // 'query' or 'action'
  const [openLatency, setOpenLatency] = useState(null);
  const messagesEndRef = useRef(null);
  const openClickStartRef = useRef(null);

  // Concise system intro
  const getInitialMessage = useCallback(() => ({
    role: 'assistant',
    content: `Connected to live workspace data. Permission-scoped operations ready.`,
    meta: {
      type: 'system',
      timestamp: new Date()
    }
  }), []);

  useEffect(() => {
    setMessages([getInitialMessage()]);
    // Load command history from localStorage
    const savedHistory = localStorage.getItem('gabhan_cmd_history');
    if (savedHistory) {
      try {
        setCommandHistory(JSON.parse(savedHistory).slice(0, 20));
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [getInitialMessage]);

  // Dispatch event when console opens/closes for FAB coordination
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('gabhan-console-toggle', { detail: { isOpen } }));
  }, [isOpen]);

  // Measure click-to-visible latency so we can validate console responsiveness.
  useEffect(() => {
    if (!isDevelopment) return;
    if (!isOpen || openClickStartRef.current === null) return;

    let raf1 = null;
    let raf2 = null;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const latencyMs = Math.round(performance.now() - openClickStartRef.current);
        setOpenLatency(latencyMs);
        logger.debug('Gabhan Console open latency', {
          clickToVisibleMs: latencyMs,
          targetMs: 100,
          meetsTarget: latencyMs <= 100,
        });
        openClickStartRef.current = null;
      });
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isDevelopment, isOpen]);

  const quickActions = [
    { label: 'Low stock', icon: 'alert' },
    { label: 'Sales today', icon: 'chart' },
    { label: 'Find product', icon: 'search' },
    { label: 'Recent orders', icon: 'list' },
    { label: 'Suppliers', icon: 'users' },
    { label: 'Warehouses', icon: 'box' },
  ];

  // Detect if command is action vs query
  const detectMode = (cmd) => {
    const actionPatterns = /^(create|add|update|delete|modify|change|set|remove|generate|export)/i;
    return actionPatterns.test(cmd.trim()) ? 'action' : 'query';
  };

  // Save command to history
  const saveToHistory = (cmd) => {
    const newHistory = [
      { command: cmd, timestamp: new Date().toISOString(), mode: detectMode(cmd) },
      ...commandHistory.filter(h => h.command !== cmd)
    ].slice(0, 20);
    setCommandHistory(newHistory);
    localStorage.setItem('gabhan_cmd_history', JSON.stringify(newHistory));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageOverride = null) => {
    const messageToSend = messageOverride || input;
    if (!messageToSend.trim() || loading) return;

    // Detect and set mode
    const mode = detectMode(messageToSend);
    setCurrentMode(mode);

    // Save to history
    saveToHistory(messageToSend);

    const userMessage = { role: 'user', content: messageToSend, mode };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setActionButtons([]);
    setSuggestions([]);
    setPendingAction(null);
    setShowHistory(false);

    const startTime = performance.now();

    try {
      const response = await api.post('/ai-chat', {
        message: messageToSend,
        conversationHistory: messages.slice(-5)
      });

      const latency = Math.round(performance.now() - startTime);
      setConnectionLatency(latency);

      const aiMessage = {
        role: 'assistant',
        content: response.data.message,
        data: response.data.data,
        intent: response.data.intent,
        meta: {
          latency,
          queryType: response.data.queryType,
          dataSource: response.data.dataSource,
          recordCount: response.data.recordCount,
          timestamp: response.data.timestamp,
          mode: response.data.queryType === 'action-prompt' ? 'action' : 'query'
        }
      };

      // Update mode based on response
      if (response.data.queryType === 'action-prompt') {
        setCurrentMode('action');
      }

      setMessages(prev => [...prev, aiMessage]);

      // Handle action confirmation requests
      if (response.data.requiresConfirmation) {
        setPendingAction(response.data.pendingAction);
      }

      // Set action buttons if provided
      if (response.data.actions && response.data.actions.length > 0) {
        setActionButtons(response.data.actions);
      }

      // Set suggestions if provided
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setSuggestions(response.data.suggestions);
      }

    } catch (error) {
      logger.apiError('/ai-chat', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Request failed. Please try again or rephrase your query.',
        isError: true,
        meta: { type: 'error', timestamp: new Date() }
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = async (confirmed) => {
    if (!pendingAction) return;

    if (confirmed) {
      // Add confirmation message
      setMessages(prev => [...prev, {
        role: 'user',
        content: 'Confirmed'
      }]);

      setLoading(true);
      try {
        const response = await api.post('/ai-chat/execute', {
          action: pendingAction
        });

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.message,
          meta: {
            type: 'action-result',
            success: response.data.success,
            timestamp: new Date()
          }
        }]);
      } catch (error) {
        logger.apiError('/ai-chat/execute', error);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Action failed. Please try again.',
          isError: true
        }]);
      } finally {
        setLoading(false);
      }
    } else {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Action cancelled.',
        meta: { type: 'cancelled' }
      }]);
    }
    setPendingAction(null);
  };

  const handleQuickAction = (action) => {
    const label = typeof action === 'string' ? action : action.label;
    setInput(label);
    setShowHistory(false);
  };

  const handleActionClick = (action) => {
    handleSend(action);
  };

  const handleHistorySelect = (cmd) => {
    setInput(cmd);
    setShowHistory(false);
  };

  const clearHistory = () => {
    setCommandHistory([]);
    localStorage.removeItem('gabhan_cmd_history');
  };

  const formatMessage = (content) => {
    // Convert markdown-style bold to HTML
    const formatted = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br />');

    return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const handleToggleConsole = () => {
    if (isDevelopment && !isOpen) {
      openClickStartRef.current = performance.now();
    }
    setIsOpen(prev => !prev);
  };

  return (
    <>
      {/* Floating Console Button */}
      <button
        onClick={handleToggleConsole}
        className="fixed bottom-6 right-6 z-[500] bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-white rounded-lg p-3.5 shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 dark:border-slate-700"
        aria-label="Open Gabhan Console"
        style={{ backgroundColor: 'var(--console-panel)', borderColor: 'var(--console-border)', color: 'var(--console-text)', marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <div className="relative">
            <Terminal className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
          </div>
        )}
      </button>

      {/* Gabhan Console Window (kept mounted for instant reopen) */}
      <div
        aria-hidden={!isOpen}
        className={`fixed bottom-24 right-6 z-[500] w-[480px] rounded-lg shadow-2xl flex flex-col max-h-[720px] transition-all duration-150 ease-out ${
          isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-2 pointer-events-none'
        }`}
        style={{
          backgroundColor: 'var(--console-bg)',
          borderColor: 'var(--console-border)',
          border: '1px solid var(--console-border)'
        }}
      >
          {/* Header */}
          <div 
            className="px-3 py-2 rounded-t-lg"
            style={{ backgroundColor: 'var(--console-header)', borderBottom: '1px solid var(--console-border)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded" style={{ backgroundColor: 'var(--console-chip)' }}>
                  <Terminal className="w-3.5 h-3.5" style={{ color: 'var(--console-text-muted)' }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-xs" style={{ color: 'var(--console-text)' }}>Gabhan Console</h3>
                    {/* Mode Indicator */}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono uppercase tracking-wider ${
                      currentMode === 'action' 
                        ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800' 
                        : 'bg-brand-100 dark:bg-brand-900/50 text-brand-700 dark:text-brand-400 border border-brand-300 dark:border-brand-800'
                    }`}>
                      {currentMode === 'action' ? (
                        <span className="flex items-center gap-1"><Zap className="w-2 h-2" />Action</span>
                      ) : (
                        <span className="flex items-center gap-1"><Search className="w-2 h-2" />Query</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[9px] flex items-center gap-0.5" style={{ color: 'var(--console-text-muted)' }}>
                      <Database className="w-2 h-2" />
                      {user?.organization?.name || 'Workspace'}
                    </span>
                    <span style={{ color: 'var(--console-text-muted)' }}>·</span>
                    <span className="text-[9px] flex items-center gap-0.5" style={{ color: 'var(--console-text-muted)' }}>
                      <Shield className="w-2 h-2" />
                      {roleLabel}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {/* History Toggle */}
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="p-1 rounded transition-colors"
                  style={{ 
                    backgroundColor: showHistory ? 'var(--console-chip)' : 'transparent',
                    color: 'var(--console-text-muted)'
                  }}
                  title="Command History"
                >
                  <History className="w-3 h-3" />
                </button>
                {connectionLatency && (
                  <span 
                    className="text-[9px] flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)', border: '1px solid var(--console-border)' }}
                  >
                    <Clock className="w-2 h-2" />
                    {connectionLatency}ms
                  </span>
                )}
                {isDevelopment && openLatency !== null && (
                  <span
                    className="text-[9px] flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)', border: '1px solid var(--console-border)' }}
                    title="Click to visible"
                  >
                    <Zap className="w-2 h-2" />
                    {openLatency}ms open
                  </span>
                )}
                <span className="text-[9px] bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded border border-green-300 dark:border-green-800 flex items-center gap-0.5">
                  <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                  Live
                </span>
              </div>
            </div>
          </div>

          {/* Command History Panel */}
          {showHistory && (
            <div 
              className="px-3 py-2 max-h-32 overflow-y-auto"
              style={{ backgroundColor: 'var(--console-panel)', borderBottom: '1px solid var(--console-border)' }}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--console-text-muted)' }}>Recent Commands</span>
                {commandHistory.length > 0 && (
                  <button 
                    onClick={clearHistory}
                    className="text-[9px] transition-colors hover:opacity-70"
                    style={{ color: 'var(--console-text-muted)' }}
                  >Clear</button>
                )}
              </div>
              {commandHistory.length === 0 ? (
                <p className="text-[10px] italic" style={{ color: 'var(--console-text-muted)' }}>No command history</p>
              ) : (
                <div className="space-y-1">
                  {commandHistory.slice(0, 8).map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleHistorySelect(item.command)}
                      className="w-full text-left flex items-center gap-2 text-[10px] px-1.5 py-1 rounded transition-colors font-mono hover:opacity-80"
                      style={{ color: 'var(--console-text)', backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--console-chip)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span className={`w-1 h-1 rounded-full ${item.mode === 'action' ? 'bg-amber-500' : 'bg-brand-500'}`}></span>
                      <span className="truncate flex-1">{item.command}</span>
                      <span className="text-[8px]" style={{ color: 'var(--console-text-muted)' }}>
                        {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <div 
            className="flex-1 overflow-y-auto p-2.5 space-y-2" 
            style={{ maxHeight: '420px', backgroundColor: 'var(--console-bg)' }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
              >
                <div
                  className={`max-w-[90%] rounded p-2.5 ${
                    msg.role === 'user'
                      ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-900 dark:text-brand-100 border border-brand-200 dark:border-brand-700'
                      : msg.isError
                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      : ''
                  }`}
                  style={msg.role !== 'user' && !msg.isError ? { backgroundColor: 'var(--console-panel)', color: 'var(--console-text)', border: '1px solid var(--console-border)' } : {}}
                >
                  {/* User command with mode badge */}
                  {msg.role === 'user' && msg.mode && (
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`text-[8px] px-1 py-0.5 rounded uppercase tracking-wider font-medium ${
                        msg.mode === 'action' ? 'bg-amber-600/50 text-amber-200' : 'bg-brand-600/50 text-brand-200'
                      }`}>
                        {msg.mode}
                      </span>
                    </div>
                  )}
                  <div className="text-xs leading-relaxed font-mono">
                    {msg.role === 'user' ? msg.content : formatMessage(msg.content)}
                  </div>

                  {/* Query Transparency Panel */}
                  {msg.meta && msg.role === 'assistant' && !msg.isError && msg.meta.queryType && (
                    <div className="mt-2 pt-1.5" style={{ borderTop: '1px solid var(--console-border)' }}>
                      <div className="flex flex-wrap gap-1 text-[9px]">
                        <span className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)' }}>
                          {msg.meta.queryType}
                        </span>
                        {msg.meta.dataSource && (
                          <span className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)' }}>
                            {msg.meta.dataSource}
                          </span>
                        )}
                        {msg.meta.recordCount !== undefined && (
                          <span className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)' }}>
                            {msg.meta.recordCount} rec
                          </span>
                        )}
                        {msg.meta.latency && (
                          <span className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)' }}>
                            {msg.meta.latency}ms
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Enhanced Pending Action Confirmation with Preview */}
            {pendingAction && !loading && (
              <div className="flex justify-start animate-fadeIn">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded p-3 max-w-[92%]">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    <span className="text-[10px] text-amber-700 dark:text-amber-300 font-medium uppercase tracking-wide">Action Preview</span>
                  </div>
                  
                  {/* Structured Preview */}
                  <div className="rounded p-2 mb-2.5" style={{ backgroundColor: 'var(--console-chip)', border: '1px solid var(--console-border)' }}>
                    <div className="space-y-1 text-[10px] font-mono">
                      {pendingAction.type && (
                        <div className="flex">
                          <span className="w-16" style={{ color: 'var(--console-text-muted)' }}>Type:</span>
                          <span className="text-amber-600 dark:text-amber-300">{pendingAction.type.replace(/_/g, ' ').toUpperCase()}</span>
                        </div>
                      )}
                      {pendingAction.target && (
                        <div className="flex">
                          <span className="w-16" style={{ color: 'var(--console-text-muted)' }}>Target:</span>
                          <span style={{ color: 'var(--console-text)' }}>{pendingAction.target}</span>
                        </div>
                      )}
                      {pendingAction.description && (
                        <div className="flex">
                          <span className="w-16" style={{ color: 'var(--console-text-muted)' }}>Action:</span>
                          <span style={{ color: 'var(--console-text)' }}>{pendingAction.description}</span>
                        </div>
                      )}
                      {pendingAction.impact && (
                        <div className="flex">
                          <span className="w-16" style={{ color: 'var(--console-text-muted)' }}>Impact:</span>
                          <span className="text-amber-600 dark:text-amber-400">{pendingAction.impact}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleConfirmAction(true)}
                      className="flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-800/80 dark:hover:bg-green-700 text-white dark:text-green-100 text-[10px] py-1.5 px-3 rounded border border-green-500 dark:border-green-700 flex items-center justify-center gap-1 transition-colors font-medium"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Execute
                    </button>
                    <button
                      onClick={() => handleConfirmAction(false)}
                      className="flex-1 text-[10px] py-1.5 px-3 rounded transition-colors"
                      style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)', border: '1px solid var(--console-border)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <div className="flex justify-start animate-fadeIn">
                <div 
                  className="rounded p-2.5"
                  style={{ backgroundColor: 'var(--console-panel)', border: '1px solid var(--console-border)' }}
                >
                  <div className="flex items-center gap-2" style={{ color: 'var(--console-text-muted)' }}>
                    <Loader className="w-3 h-3 animate-spin" />
                    <span className="text-[10px] font-mono">
                      {currentMode === 'action' ? 'Preparing action...' : 'Executing query...'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Action Buttons - Outline Style */}
          {actionButtons.length > 0 && !loading && !pendingAction && (
            <div 
              className="px-2.5 py-2"
              style={{ backgroundColor: 'var(--console-panel)', borderTop: '1px solid var(--console-border)' }}
            >
              <p className="text-[9px] uppercase tracking-wide mb-1" style={{ color: 'var(--console-text-muted)' }}>Actions</p>
              <div className="flex flex-wrap gap-1">
                {actionButtons.slice(0, 4).map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleActionClick(action)}
                    className="text-[10px] bg-transparent hover:opacity-80 rounded px-2 py-1 transition-colors font-mono"
                    style={{ color: 'var(--console-text)', border: '1px solid var(--console-border)' }}
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions - Subtle Links */}
          {suggestions.length > 0 && !loading && !pendingAction && (
            <div 
              className="px-2.5 py-1.5"
              style={{ backgroundColor: 'var(--console-panel)', borderTop: '1px solid var(--console-border)' }}
            >
              <div className="flex flex-wrap gap-2">
                <span className="text-[9px]" style={{ color: 'var(--console-text-muted)' }}>Related:</span>
                {suggestions.slice(0, 3).map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(suggestion)}
                    className="text-[10px] transition-colors font-mono underline underline-offset-2 hover:opacity-70"
                    style={{ color: 'var(--console-text-muted)' }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick Commands - Compact Grid */}
          {messages.length === 1 && !showHistory && (
            <div 
              className="px-2.5 py-2"
              style={{ backgroundColor: 'var(--console-panel)', borderTop: '1px solid var(--console-border)' }}
            >
              <p className="text-[9px] uppercase tracking-wide mb-1.5" style={{ color: 'var(--console-text-muted)' }}>Commands</p>
              <div className="grid grid-cols-3 gap-1">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(action)}
                    className="text-[10px] bg-transparent hover:opacity-80 rounded px-1.5 py-1 text-left transition-colors font-mono"
                    style={{ color: 'var(--console-text-muted)', border: '1px solid var(--console-border)' }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div 
            className="px-2.5 py-2 rounded-b-lg"
            style={{ backgroundColor: 'var(--console-header)', borderTop: '1px solid var(--console-border)' }}
          >
            <div className="flex gap-1.5">
              <input
                type="text"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setCurrentMode(detectMode(e.target.value));
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                onFocus={() => setShowHistory(false)}
                placeholder="Enter command..."
                className="flex-1 px-2.5 py-1.5 rounded-sm focus:ring-1 focus:ring-brand-500/50 focus:border-brand-500/50 text-xs font-mono"
                style={{ 
                  backgroundColor: 'var(--console-input-bg)', 
                  color: 'var(--console-text)', 
                  border: '1px solid var(--console-border)',
                }}
                disabled={loading || pendingAction}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading || pendingAction}
                className="bg-brand-600 hover:bg-brand-700 text-white rounded-sm px-2.5 py-1.5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-brand-500"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[9px] mt-1.5 flex items-center justify-between" style={{ color: 'var(--console-text-muted)' }}>
              <span className="flex items-center gap-1">
                <Shield className="w-2 h-2" />
                Scoped to workspace
              </span>
              <span className="flex items-center gap-2">
                <kbd className="px-1 py-0.5 rounded text-[8px]" style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)' }}>↑</kbd>
                <span>history</span>
                <kbd className="px-1 py-0.5 rounded text-[8px]" style={{ backgroundColor: 'var(--console-chip)', color: 'var(--console-text-muted)' }}>⏎</kbd>
                <span>execute</span>
              </span>
            </div>
          </div>
        </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ChatAssistant;
